import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Label } from '@/features/shared/components/ui/label';
import { payrollApi } from '../api/payrollApi';
import { getPayrollReadiness } from '@/services/payrollSettingsService';
import type { LegalEntityLite, PayrollRun, RunState } from '../types';
import { COUNTRY_FLAG } from '../lib/format';

interface NewRunDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (run: PayrollRun) => void;
}

const selectClass =
  'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-orange-500/40';

// The dialog's own guidance sends users elsewhere first (e.g. "assign them in the Payroll
// Roster"), so an in-progress run selection must survive navigating away and back. Persist the
// draft to sessionStorage (per-tab, cleared on tab close) and restore it when the dialog reopens.
const DRAFT_KEY = 'kp:payroll:newRunDraft';
type RunDraft = { legalEntityId?: string; month?: string; label?: string };
const readDraft = (): RunDraft => {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || '{}') as RunDraft;
  } catch {
    return {};
  }
};

// Plain words for where the existing run stands, so the person can tell at a
// glance whether it is a fresh draft or someone's validated, half-approved work.
const STATE_WORDS: Record<RunState, string> = {
  draft: 'a draft',
  validated: 'calculated and waiting for approval',
  approved: 'approved',
  locked: 'locked',
  paid: 'paid',
  cancelled: 'cancelled',
};

/** The sentence shown when Create returned a run that already existed. Exported for tests. */
export function existingRunMessage(run: PayrollRun, typedLabel: string): string {
  const name = run.label ? `“${run.label}”, ` : '';
  const where = `${run.legalEntityName && run.legalEntityName !== '—' ? run.legalEntityName : 'This company'} already has a payroll run for ${run.periodLabel}`;
  const state = STATE_WORDS[run.state] ?? run.state;
  const parts = [
    `${where} (${name}${state}). No new run was created: a company has one run per pay period.`,
  ];
  if (run.state === 'cancelled') {
    parts.push('A cancelled run still holds its period.');
  }
  if (typedLabel.trim() && typedLabel.trim() !== (run.label ?? '')) {
    parts.push('The name you typed was not applied.');
  }
  parts.push('Open the existing run, or pick a different month.');
  return parts.join(' ');
}

function monthBounds(month: string): { start: string; end: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, '0');
  return { start: `${y}-${mm}-01`, end: `${y}-${mm}-${String(last).padStart(2, '0')}` };
}

export const NewRunDialog: React.FC<NewRunDialogProps> = ({ open, onOpenChange, onCreated }) => {
  const { t } = useTranslation('payroll');
  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [legalEntityId, setLegalEntityId] = useState(() => readDraft().legalEntityId || '');
  const [month, setMonth] = useState(() => readDraft().month || '');
  const [label, setLabel] = useState(() => readDraft().label || '');
  const [submitting, setSubmitting] = useState(false);
  // Create is idempotent: a period that already has a run returns THAT run. It
  // is held here — never announced as "created" — until the person chooses to
  // open it (RT1: an earlier session's validated run was opened as if new).
  const [existing, setExisting] = useState<PayrollRun | null>(null);
  // DEF-0mvn3tgtl005: the headcount is the people IN this company's payroll for
  // the PERIOD — payroll's own period population (handed-over employments
  // still owed, start/end dates), not today's roster. They are the run's
  // CANDIDATES, not a promise that each one calculates: someone missing a
  // salary or statutory IDs still errors, and the run's Employees tab shows
  // those separately (HO-0mvofhjd4001-46ac7a). Warn (don't just fail) when it
  // is nobody. 'error' is unknown, never "0".
  const [headcount, setHeadcount] = useState<number | 'loading' | 'error' | null>(null);
  const headcountSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setExisting(null);
    payrollApi
      .listLegalEntities()
      .then((es) => {
        setEntities(es);
        setLegalEntityId((cur) => cur || es[0]?.id || '');
      })
      .catch(() => setEntities([]));
  }, [open]);

  useEffect(() => {
    const seq = ++headcountSeq.current;
    if (!open || !legalEntityId || !month) {
      setHeadcount(null);
      return;
    }
    setHeadcount('loading');
    const { start, end } = monthBounds(month);
    getPayrollReadiness({ legalEntityId, periodStart: start, periodEnd: end })
      .then((r) => {
        if (seq !== headcountSeq.current) return; // a newer company/month owns the answer
        setHeadcount(r.employees.filter((e) => e.status !== 'excluded').length);
      })
      .catch(() => {
        if (seq === headcountSeq.current) setHeadcount('error');
      });
  }, [open, legalEntityId, month]);

  // Keep the draft in sync so it survives navigating to another tab and back.
  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ legalEntityId, month, label }));
  }, [legalEntityId, month, label]);

  // A different company or month is a different question: drop the old answer.
  useEffect(() => {
    setExisting(null);
  }, [legalEntityId, month]);


  const submit = async () => {
    if (!legalEntityId || !month) {
      toast.error(t('errors.pickRunInputs'));
      return;
    }
    setSubmitting(true);
    try {
      const { start, end } = monthBounds(month);
      const { run, created } = await payrollApi.createRun({ legalEntityId, periodStart: start, periodEnd: end, label });
      if (!created) {
        // Not a success and not a failure: say what exists and let them choose.
        setExisting(run);
        return;
      }
      toast.success(t('run.created'));
      onOpenChange(false);
      setMonth('');
      // The name belonged to the run just created; carrying it into the next one
      // is how the second run silently inherits the first one's name.
      setLabel('');
      onCreated(run);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('errors.createRun')), { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('newRun.title')}</DialogTitle>
          <DialogDescription>{t('newRun.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="legal-entity">{t('newRun.legalEntity')}</Label>
            {entities.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('newRun.noEntities')}
              </p>
            ) : (
              <select
                id="legal-entity"
                className={selectClass}
                value={legalEntityId}
                onChange={(e) => setLegalEntityId(e.target.value)}
              >
                {entities.map((le) => (
                  <option key={le.id} value={le.id}>
                    {COUNTRY_FLAG[le.country]} {le.name} · {le.currency}
                  </option>
                ))}
              </select>
            )}
            {headcount === 'error' && (
              <p className="text-xs text-muted-foreground" data-testid="new-run-headcount">
                Couldn&apos;t check who is in this period&apos;s payroll. You can still create the run.
              </p>
            )}
            {typeof headcount === 'number' && (
              headcount === 0 ? (
                <p className="text-xs text-warning" data-testid="new-run-headcount">
                  Nobody at this entity is in this period&apos;s payroll — this run would be empty. Check
                  their employment dates, or assign them in Payroll Settings → Payroll Roster.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="new-run-headcount">
                  {headcount} {headcount === 1 ? 'person is' : 'people are'} in this period&apos;s payroll.
                  Anyone missing a salary or statutory IDs shows as an error when you calculate (the
                  run&apos;s Employees tab lists them).
                </p>
              )
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="period-month">{t('newRun.payPeriod')}</Label>
            <input
              id="period-month"
              type="month"
              className={selectClass}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="run-label">
              {t('newRun.name', { defaultValue: 'Name' })}{' '}
              <span className="font-normal text-muted-foreground">
                {t('newRun.optional', { defaultValue: '(optional)' })}
              </span>
            </Label>
            <input
              id="run-label"
              className={selectClass}
              value={label}
              maxLength={80}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('newRun.namePlaceholder', { defaultValue: 'e.g. Bonus run, May correction' })}
            />
            {/* Naming matters most for the SECOND run in a month, which is
                exactly when the list is otherwise ambiguous. Say so here rather
                than leaving people to discover it from a confusing list later. */}
            <p className="text-xs text-muted-foreground">
              {t('newRun.nameHelp', {
                defaultValue:
                  'Helps tell this run apart from others for the same month. It does not change the pay period.',
              })}
            </p>
          </div>

          {existing && (
            <div
              role="alert"
              className="space-y-2 rounded-lg border border-warning/40 bg-warning-soft p-3 text-sm text-warning-strong"
            >
              <p>{existingRunMessage(existing, label)}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const run = existing;
                  onOpenChange(false);
                  setExisting(null);
                  onCreated(run);
                }}
              >
                {t('newRun.openExisting', { defaultValue: 'Open the existing run' })}
              </Button>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('newRun.cancel')}
            </Button>
            <Button onClick={submit} disabled={submitting || entities.length === 0}>
              {submitting ? t('newRun.creating') : t('newRun.createRun')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
